'use client';

import React from 'react';
import NumberDisplay from './NumberDisplay/NumberDisplay';
import { Swap } from '@velocity-exchange/icons';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { UIMarket, ENUM_UTILS } from '@velocity-exchange/common';
import {
	BASE_PRECISION_EXP,
	BigNum,
	MarketStatus,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import ApyBreakdownTooltipV2 from './Tooltip/ApyBreakdownTooltipV2';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import { cj } from 'src/utils/classNames';
import MarketIcon from './Utils/MarketIcon';
import { useBalanceRowData } from '../hooks/useBalanceRowData';
import { useApyBreakdownTooltipData } from '../hooks/useApyBreakdownTooltipData';
import Button from './primitives/Button/Button';
import useModal from 'src/components/Modals/hooks/useModal';
import { BalanceDisplayType } from 'src/@types/types';
import MobileTableCard from './Tables/cards/MobileTableCard';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import { PRESETS, legacyTrim } from 'src/utils/formatNumber/presets';

interface AssetBalanceCardProps {
	spotMarketIndex: number;
	highlighted: boolean;
	balanceDisplayType: BalanceDisplayType;
	onToggleBalanceDisplayType: () => void;
	marketIsProtected?: boolean;
	isPortfolioPage?: boolean;
	base90DayEarning: BigNum | undefined;
	quote90DayEarning: BigNum | undefined;
}

const AssetBalanceCardV2 = ({
	spotMarketIndex,
	highlighted,
	balanceDisplayType: valueDisplayType,
	onToggleBalanceDisplayType: onToggleValueDisplayType,
	marketIsProtected = false,
	isPortfolioPage = false,
	base90DayEarning,
	quote90DayEarning,
}: AssetBalanceCardProps) => {
	const userBalanceInfo = useBalanceRowData({
		spotMarketIndex: spotMarketIndex,
		highlighted: highlighted,
	});
	const { show } = useModal();
	const velocityClient = useVelocityClient();
	const subaccounts = useTickedAllSubAccounts();
	const { isPrivyNativeAuth } = usePrivyWalletContext();
	const apyData = useApyBreakdownTooltipData(spotMarketIndex);

	// Logic from BalanceRowDisplay for determining button states
	const spotMarketAccount = React.useMemo(
		() => velocityClient?.getSpotMarketAccount(spotMarketIndex),
		[velocityClient, spotMarketIndex]
	);

	const isMarketInReduceOnly = React.useMemo(
		() => ENUM_UTILS.match(spotMarketAccount?.status, MarketStatus.REDUCE_ONLY),
		[spotMarketAccount]
	);

	const userHasAccountInPool = React.useMemo(
		() => subaccounts.some((acc) => acc?.poolId === spotMarketAccount?.poolId),
		[subaccounts, spotMarketAccount?.poolId]
	);

	const borrowDisabled =
		marketIsProtected || !userHasAccountInPool || isPrivyNativeAuth;

	const isBorrow = userBalanceInfo?.borrowsBase.gtZero();
	const hasUserSpotBalance = isBorrow
		? userBalanceInfo?.borrowsBase.gtZero()
		: userBalanceInfo?.depositsBase.gtZero();

	const displayWithdrawButton = hasUserSpotBalance && !isBorrow;
	const displayRepayButton = hasUserSpotBalance && isBorrow;
	const displayDepositButton = !isMarketInReduceOnly && !displayRepayButton;
	const displayBorrowButton =
		!borrowDisabled && !isMarketInReduceOnly && !displayWithdrawButton;

	const handleCloseBorrow = () => {
		show({
			type: 'repay',
			props: {
				collateralIndex: userBalanceInfo?.spotMarketIndex,
			},
		});
	};

	const handleDeposit = () => {
		show({
			type: 'deposit',
			props: {
				collateralIndex: userBalanceInfo?.spotMarketIndex,
			},
		});
	};

	const handleBorrow = () => {
		show({
			type: 'borrow',
			props: {
				collateralIndex: userBalanceInfo?.spotMarketIndex,
			},
		});
	};

	const handleWithdraw = () => {
		show({
			type: 'withdraw',
			props: {
				collateralIndex: userBalanceInfo?.spotMarketIndex,
			},
		});
	};

	const userBalanceInfoUISpotMarketConfig = UIMarket.createSpotMarket(
		userBalanceInfo?.spotMarketIndex as number
	);

	const isPositiveTotalBorrowApy = apyData.borrowing.totalApy > 0;

	return (
		<MobileTableCard.Root className="w-full gap-y-lg overflow-hidden text-text-default">
			<div className="flex items-center gap-x-sm">
				<MarketIcon
					className="size-5"
					marketSymbol={
						userBalanceInfoUISpotMarketConfig.uiSymbols.marketDisplaySymbol
					}
				/>
				<span className="line-clamp-1 max-w-[200px] overflow-hidden typo-label-md text-wrap text-ellipsis text-text-default">
					{userBalanceInfoUISpotMarketConfig.uiSymbols.marketDisplaySymbol}
				</span>
			</div>

			<div
				className={cj(
					'grid',
					isPortfolioPage
						? 'grid-cols-2 gap-y-lg'
						: 'grid-cols-3 justify-between'
				)}
			>
				<div className="flex flex-col items-start gap-y-xs">
					<div className="flex gap-x-xs">
						<MobileTableCard.Label>
							{valueDisplayType === 'base' ? 'Balance' : 'Value'}
						</MobileTableCard.Label>
						<IconButton
							size="sm"
							variant="tertiary"
							className="-mr-md -ml-2xl pr-md pl-2xl text-text-tertiary"
							onClick={onToggleValueDisplayType}
							icon={Swap}
						/>
					</div>
					<div className="typo-label-xs">
						{valueDisplayType === 'base' ? (
							<NumberDisplay
								value={userBalanceInfo?.netBase as BigNum}
								options={legacyTrim(
									PRESETS.tradePrecisionLegacy,
									userBalanceInfo?.netBase as BigNum
								)}
								isAccountValueToHide
							/>
						) : (
							<NumberDisplay
								value={userBalanceInfo?.netQuote as BigNum}
								options={PRESETS.usd}
								isAccountValueToHide
							/>
						)}
					</div>
				</div>

				{isPortfolioPage && (
					<div className="flex flex-col items-end gap-y-xs">
						<div className="flex gap-x-xs">
							<MobileTableCard.Label>90D Earnings</MobileTableCard.Label>
							<IconButton
								size="sm"
								variant="tertiary"
								className="-mr-md -ml-2xl pr-md pl-2xl text-text-tertiary"
								onClick={onToggleValueDisplayType}
								icon={Swap}
							/>
						</div>
						<div className="typo-label-xs">
							{valueDisplayType === 'base' ? (
								<NumberDisplay
									value={base90DayEarning ?? BigNum.zero(BASE_PRECISION_EXP)}
									options={legacyTrim(
										PRESETS.tradePrecisionLegacy,
										base90DayEarning ?? BigNum.zero(BASE_PRECISION_EXP)
									)}
									isAccountValueToHide
								/>
							) : (
								<NumberDisplay
									value={quote90DayEarning ?? BigNum.zero(QUOTE_PRECISION_EXP)}
									options={PRESETS.usd}
									isAccountValueToHide
								/>
							)}
						</div>
					</div>
				)}

				<div
					className={cj(
						'flex flex-col gap-y-xs',
						isPortfolioPage ? 'items-start' : 'items-center'
					)}
				>
					<div className="flex gap-x-xs">
						<MobileTableCard.Label>Earning APY</MobileTableCard.Label>
					</div>
					<div className="typo-label-xs">
						<ApyBreakdownTooltipV2
							apyChunks={apyData.deposit.chunks}
							showTooltip={apyData.deposit.showTooltip}
						>
							<span className="pr-1 text-text-positive-green-button">
								{apyData.deposit.totalApy.toFixed(2)}%
							</span>
						</ApyBreakdownTooltipV2>
					</div>
				</div>

				<div className="flex flex-col items-end gap-y-xs">
					<div className="flex gap-x-xs">
						<MobileTableCard.Label>Borrow APY</MobileTableCard.Label>
					</div>
					<div className="typo-label-xs">
						<ApyBreakdownTooltipV2
							apyChunks={apyData.borrowing.chunks}
							showTooltip={apyData.borrowing.showTooltip}
						>
							<span
								className={
									isPositiveTotalBorrowApy
										? 'text-star-yellow'
										: 'text-text-positive-green-button'
								}
							>
								{velocityClient
									? Math.abs(apyData.borrowing.totalApy).toFixed(2)
									: '0.00'}
								%
							</span>
						</ApyBreakdownTooltipV2>
					</div>
				</div>
			</div>

			<div className="flex justify-between gap-x-sm">
				{displayRepayButton ? (
					<Button
						variant="secondary"
						size="sm"
						onClick={handleCloseBorrow}
						className="w-1/2"
					>
						Repay
					</Button>
				) : displayDepositButton ? (
					<Button
						variant="secondary"
						size="sm"
						onClick={handleDeposit}
						className="w-1/2"
					>
						Deposit
					</Button>
				) : null}

				{displayWithdrawButton ? (
					<Button
						variant="secondary"
						size="sm"
						onClick={handleWithdraw}
						className="w-1/2"
					>
						Withdraw
					</Button>
				) : displayBorrowButton ? (
					<Button
						variant="secondary"
						size="sm"
						onClick={handleBorrow}
						className="w-1/2"
					>
						Borrow
					</Button>
				) : null}
			</div>
		</MobileTableCard.Root>
	);
};

export default React.memo(AssetBalanceCardV2);
